import React, { useMemo } from 'react'
import { Box, Button, Chip, Typography, Alert, Stack } from '@mui/material'
import PaymentIcon from '@mui/icons-material/Payment'
import LockIcon from '@mui/icons-material/Lock'
import InfoIcon from '@mui/icons-material/Info'
import { useAccountStatus } from '../../hooks/useAccountStatus'
import { useRoutingStore } from '../../stores/routingStore'

/**
 * SubscriptionRequiredOverlay
 * Full-screen professional overlay shown when a Company Manager/Admin
 * must subscribe to continue (ran out of free slots or no active plan).
 *
 * Blocks all interactions except navigation to Company Management/Billing.
 */
const SubscriptionRequiredOverlay: React.FC = () => {
  const {
    accountStatus,
    subscriptionStatus,
    paymentStatus,
    statusMessage,
    actionMessage,
    refresh,
  } = useAccountStatus()

  const routingStore = useRoutingStore()

  const primaryMessage = useMemo(() => {
    if (actionMessage) return actionMessage
    if (statusMessage) return statusMessage
    if (subscriptionStatus === 'free_tier_exceeded') {
      return 'Your company has exceeded the free plan limits. Please subscribe to continue.'
    }
    return 'Your company has reached the free plan limits. Please subscribe to continue.'
  }, [actionMessage, statusMessage, subscriptionStatus])

  const handleOpenCompany = async () => {
    // If user has no company yet, go to setup, otherwise company dashboard
    const hasCompany = !!accountStatus?.company
    routingStore.navigateTo(hasCompany ? '/company' : '/company/setup')
    // Refresh status after navigation so overlay can disappear if conditions improve
    setTimeout(() => {
      refresh().catch(() => {})
    }, 50)
  }

  const handleOpenBilling = async () => {
    const hasCompany = !!accountStatus?.company
    routingStore.navigateTo(hasCompany ? '/company/billing' : '/company/setup')
    setTimeout(() => {
      refresh().catch(() => {})
    }, 50)
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.70) 0%, rgba(0,0,0,0.88) 100%)',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: 'min(780px, 92vw)',
          borderRadius: 3,
          p: 4,
          bgcolor: 'rgba(16, 23, 32, 0.9)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 12px 48px rgba(0,0,0,0.5)',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <LockIcon sx={{ color: '#f44336' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Subscription Required
          </Typography>
        </Stack>

        <Alert severity="warning" icon={<InfoIcon /> } sx={{ mb: 3 }}>
          <Typography variant="body1">
            {primaryMessage}
          </Typography>
        </Alert>

        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap' }}>
          <Chip
            label={`Subscription: ${subscriptionStatus === 'free_tier_exceeded' ? 'Free Tier Exceeded' : 
                    subscriptionStatus === 'free_tier_active' ? 'Free Tier Active' : 
                    subscriptionStatus || 'unknown'}`}
            variant="outlined"
            sx={{ color: 'rgba(255,255,255,0.9)', borderColor: 'rgba(255,255,255,0.2)' }}
          />
          <Chip
            label={`Payment: ${paymentStatus || 'unknown'}`}
            variant="outlined"
            sx={{ color: 'rgba(255,255,255,0.9)', borderColor: 'rgba(255,255,255,0.2)' }}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {accountStatus?.company ? (
            <Button
              onClick={handleOpenBilling}
              variant="contained"
              color="primary"
              startIcon={<PaymentIcon />}
              sx={{
                fontWeight: 700,
                px: 3,
                py: 1.2,
              }}
            >
              Manage Billing
            </Button>
          ) : null}
          <Button
            onClick={handleOpenCompany}
            variant="outlined"
            color="inherit"
            sx={{ fontWeight: 600, px: 3, py: 1.2 }}
          >
            {accountStatus?.company ? 'Company Management' : 'Set Up Your Company'}
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}

export default SubscriptionRequiredOverlay


